import { useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LayoutSubHeader, LayoutPageTitle } from '@/features/layout';
import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/Tabs';
import { Divider } from '@/shared/ui/Divider';
import {
  AppearanceSection,
  ReportingSection,
  SecuritySection,
  ViewerSection,
  WorkflowSection,
  useSettings,
  useChangePassword,
} from '@/features/settings';
import styles from './SettingsPage.module.css';

type TabKey =
  | 'appearance'
  | 'viewer'
  | 'reporting'
  | 'workflow'
  | 'notifications'
  | 'security';

const SettingsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('appearance');

  const { settings, darkTheme, updateSetting } = useSettings();
  const { pwForm, changingPassword, onChangePassword } = useChangePassword();

  // Scroll to section or individual setting on mount/hash change
  useLayoutEffect(() => {
    const hash = location.hash.replace('#', '');
    if (!hash) return;

    const sectionTabs: TabKey[] = [
      'appearance',
      'viewer',
      'reporting',
      'workflow',
      'notifications',
      'security',
    ];

    // Activate the correct tab
    if (sectionTabs.includes(hash as TabKey)) {
      setActiveTab(hash as TabKey);
    }

    const target = document.getElementById(hash);
    const container = scrollRef.current;
    if (!target || !container) return;

    // For setting-level anchors, activate the parent section tab
    if (!sectionTabs.includes(hash as TabKey)) {
      const section = target.closest('section[id]');
      if (section) {
        setActiveTab(section.id as TabKey);
      }
    }

    container.scrollTo({
      top: target.offsetTop - container.offsetTop,
      behavior: 'smooth',
    });
  }, [location.hash]);

  const onTabChange = (key: string) => {
    setActiveTab(key as TabKey);
    navigate(`#${key}`, { replace: true });

    const target = document.getElementById(key);
    const container = scrollRef.current;
    if (target && container) {
      container.scrollTo({
        top: target.offsetTop - container.offsetTop,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      <LayoutPageTitle title="Settings" />
      <LayoutSubHeader>
        <Tabs value={activeTab} onValueChange={onTabChange}>
          <TabsList>
            <TabsTrigger value="appearance">Appearance</TabsTrigger>
            <TabsTrigger value="viewer">Viewer</TabsTrigger>
            <TabsTrigger value="reporting">Reporting</TabsTrigger>
            <TabsTrigger value="workflow">Workflow</TabsTrigger>
            <TabsTrigger value="notifications">Notifications</TabsTrigger>
            <TabsTrigger value="security">Account and Security</TabsTrigger>
          </TabsList>
        </Tabs>
      </LayoutSubHeader>

      <div ref={scrollRef} className={styles.scroll}>
        <div className={styles.content}>
          <section id="appearance" className={styles.section}>
            <h3 className={styles.sectionTitle}>Appearance</h3>
            <p className={styles.sectionDescription}>
              Control the look and feel of the interface.
            </p>
            <AppearanceSection
              settings={settings}
              darkTheme={darkTheme}
              onUpdate={updateSetting}
            />
          </section>

          <section id="viewer" className={styles.section}>
            <h3 className={styles.sectionTitle}>Viewer</h3>
            <p className={styles.sectionDescription}>
              Viewer preferences and defaults.
            </p>
            <ViewerSection settings={settings} onUpdate={updateSetting} />
          </section>

          <section id="reporting" className={styles.section}>
            <h3 className={styles.sectionTitle}>Reporting</h3>
            <p className={styles.sectionDescription}>
              Defaults applied when you open a report.
            </p>
            <ReportingSection settings={settings} onUpdate={updateSetting} />
          </section>

          <section id="workflow" className={styles.section}>
            <h3 className={styles.sectionTitle}>Workflow</h3>
            <p className={styles.sectionDescription}>
              Worklist and task preferences.
            </p>
            <WorkflowSection settings={settings} onUpdate={updateSetting} />
          </section>

          <section id="notifications" className={styles.section}>
            <h3 className={styles.sectionTitle}>Notifications</h3>
            <p className={styles.sectionDescription}>
              Email and in-app notification settings.
            </p>
            <span className={styles.placeholder}>Coming soon.</span>
          </section>

          <Divider />

          <section id="security" className={styles.section}>
            <h3 className={styles.sectionTitle}>Account and Security</h3>
            <p className={styles.sectionDescription}>
              Manage your password and account settings.
            </p>
            <SecuritySection
              pwForm={pwForm}
              changingPassword={changingPassword}
              onChangePassword={onChangePassword}
              settings={settings}
              onUpdate={updateSetting}
            />
          </section>
        </div>
      </div>
    </>
  );
};

export default SettingsPage;
